import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/fetch';
import { useAuth } from './AuthContext';
import './TermsModal.css';

type SupportedLang = 'ko' | 'en';

function resolveLang(raw: string | undefined): SupportedLang {
  return raw === 'en' ? 'en' : 'ko';
}

export function TermsModal() {
  const { t } = useTranslation('auth');
  const { lang: langParam } = useParams<{ lang?: string }>();
  const lang = resolveLang(langParam);
  const { user, termsUpdateRequired, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);

  const open = Boolean(user && termsUpdateRequired);

  useEffect(() => {
    if (open) acceptButtonRef.current?.focus();
  }, [open]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/api/auth/terms-accept', { method: 'POST' });
      if (!res.ok) {
        setError(t('terms.error'));
        return;
      }
      await refresh();
    } catch {
      setError(t('terms.error'));
    } finally {
      setSubmitting(false);
    }
  }, [refresh, t]);

  if (!open) return null;

  return (
    <div className="terms-modal-backdrop" role="presentation">
      <div
        className="terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
      >
        <h2 id="terms-modal-title" className="terms-modal-title">
          {t('terms.title')}
        </h2>
        <p className="terms-modal-intro">{t('terms.intro')}</p>
        <ul className="terms-modal-links">
          <li>
            <Link
              to={`/${lang}/terms`}
              target="_blank"
              rel="noreferrer"
              className="terms-modal-link"
            >
              {t('terms.termsLink')}
            </Link>
          </li>
          <li>
            <Link
              to={`/${lang}/privacy`}
              target="_blank"
              rel="noreferrer"
              className="terms-modal-link"
            >
              {t('terms.privacyLink')}
            </Link>
          </li>
        </ul>
        {error && (
          <div className="terms-modal-error" role="alert">
            {error}
          </div>
        )}
        <button
          ref={acceptButtonRef}
          type="button"
          className="terms-modal-accept"
          onClick={handleAccept}
          disabled={submitting}
        >
          {submitting ? t('terms.submitting') : t('terms.accept')}
        </button>
      </div>
    </div>
  );
}
